import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStrapi } from '../hooks/useStrapi';
import { getPageBySlug, getServices, getPackagePrices, getCtaBlock, getBlogPosts, getNewsItems } from '../services/strapi';
import ServiceCard from '../components/ServiceCard';
import PricingCard from '../components/PricingCard';

const NEWS_PAGE_SIZE = 3;
const NEWS_PREVIEW_LENGTH = 150;

// Compliance frameworks the platform covers — shown as hero trust badges.
const FRAMEWORKS = ['NIS2', 'ISO 27001', 'CIS 18', 'CRA', 'IEC 62443', 'GDPR'];

function formatDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleDateString('da-DK', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function HomePage() {
  const { data: pageData } = useStrapi(() => getPageBySlug('forside'));
  const { data: servicesData } = useStrapi(getServices);
  const { data: pricesData } = useStrapi(getPackagePrices);
  const { data: ctaData } = useStrapi(getCtaBlock);
  const { data: blogData } = useStrapi(() => getBlogPosts(3));
  const { data: newsData } = useStrapi(() => getNewsItems(20));

  const [visibleNewsCount, setVisibleNewsCount] = useState(NEWS_PAGE_SIZE);

  const page = pageData?.[0]?.attributes || {};
  const services = servicesData || [];
  const prices = pricesData || [];
  const ctaBlock = ctaData?.attributes || ctaData || {};
  const blogs = blogData || [];
  const news = newsData || [];

  const visibleNews = news.slice(0, visibleNewsCount);
  const hasMoreNews = visibleNewsCount < news.length;

  return (
    <>
      <section className="hero">
        <div className="container">
          <span className="hero__pill"><span className="dot"></span> Cybersikkerhed &amp; compliance for danske SMV'er</span>
          <h1 className="hero__title">{page.heroTitle || 'Sikkerhed og compliance'}</h1>
          <p className="hero__subtitle">{page.heroSubtitle}</p>
          <div className="hero__actions">
            <Link to="/priser" className="btn btn--primary">Se priser</Link>
            <Link to="/services" className="btn btn--outline">Vores services</Link>
            <a
              href="https://complianceplatform.dk/nis2-tjek"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--outline"
            >
              Tjek om I er omfattet af NIS2
            </a>
          </div>
          <div className="hero__badges">
            {FRAMEWORKS.map((fw) => (
              <span key={fw} className="hero__badge">{fw}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <p className="section__eyebrow">Vores services</p>
          <h2 className="section__title">Fra gratis screening til fuld compliance</h2>
          <div className="cards">
            {services.map((svc, i) => <ServiceCard key={i} service={svc} />)}
          </div>
        </div>
      </section>

      <section className="section section--alt" id="blog">
        <div className="container">
          <p className="section__eyebrow">Viden</p>
          <h2 className="section__title">Seneste fra Bloggen</h2>
          <div className="cards">
            {blogs.map((post, i) => (
              <div key={i} className="card">
                <h3 className="card__title">{post.attributes.title}</h3>
                <p className="card__text">{post.attributes.author}</p>
                <Link to={'/blog/' + post.attributes.slug} className="btn btn--link">Læs mere →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="nyheder">
        <div className="container">
          <p className="section__eyebrow">Aktuelt</p>
          <h2 className="section__title">Sidste Nyt</h2>
          <div className="news-list">
            {visibleNews.map((item) => {
              const content = item.attributes.content || '';
              const isTruncated = content.length > NEWS_PREVIEW_LENGTH;
              const preview = isTruncated ? content.substring(0, NEWS_PREVIEW_LENGTH) + '...' : content;
              return (
                <div key={item.id} className="news-item-row" style={{ marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '0.25rem' }}>
                    {formatDate(item.attributes.publishedAt)}
                  </div>
                  <p style={{ whiteSpace: 'pre-line', marginBottom: '0.5rem' }}>{preview}</p>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    {isTruncated && (
                      <Link to={`/nyheder/${item.id}`} className="btn btn--link">
                        Læs hele nyheden →
                      </Link>
                    )}
                    {item.attributes.url && (
                      <a
                        href={item.attributes.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn--link"
                      >
                        Se originalt opslag →
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {hasMoreNews && (
            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <button
                onClick={() => setVisibleNewsCount((c) => c + NEWS_PAGE_SIZE)}
                className="btn btn--ghost"
                style={{ cursor: 'pointer' }}
              >
                Vis flere
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="section section--alt">
        <div className="container">
          <p className="section__eyebrow">Priser</p>
          <h2 className="section__title">Vælg den rette pakke</h2>
          <div className="pricing">
            {prices.map((pkg, i) => <PricingCard key={i} pkg={pkg} />)}
          </div>
        </div>
      </section>

      {ctaBlock.isActive !== false && (
        <section className="hero" style={{ padding: '3.5rem 0' }}>
          <div className="container">
            <h2 className="hero__title" style={{ fontSize: '2rem' }}>{ctaBlock.heading}</h2>
            <Link to="/kontakt" className="btn btn--primary">Kontakt os</Link>
          </div>
        </section>
      )}
    </>
  );
}
